import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Loader2, ImageIcon, Plus, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

export function LogoGalleryCard({ restaurantId }) {
  const [logoGallery, setLogoGallery] = useState([]);
  const [activeLogo, setActiveLogo] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);

  useEffect(() => {
    if (!restaurantId) return;
    api.getLogoGallery(restaurantId)
      .then(res => { setLogoGallery(res.logos || []); setActiveLogo(res.active_logo || null); })
      .catch(() => {});
  }, [restaurantId]);

  const handleUploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Máximo 5MB"); return; }
    setUploadingLogo(true);
    try {
      const up = await api.uploadImage(file);
      if (up.success) {
        const newUrl = up.image_url;
        const newGallery = [newUrl, ...logoGallery.filter(u => u !== newUrl)].slice(0, 10);
        await api.updateLogoGallery(restaurantId, newGallery, newUrl);
        setLogoGallery(newGallery);
        setActiveLogo(newUrl);
        toast.success("Logo subido y activado");
      }
    } catch {
      toast.error("Error al subir logo");
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = "";
    }
  };

  const handleSetActiveLogo = async (url) => {
    try {
      await api.updateLogoGallery(restaurantId, logoGallery, url);
      setActiveLogo(url);
      toast.success("Logo activado");
    } catch { toast.error("Error al activar logo"); }
  };

  const handleRemoveLogo = async (url) => {
    const newGallery = logoGallery.filter(u => u !== url);
    const newActive = activeLogo === url ? (newGallery[0] || null) : activeLogo;
    await api.updateLogoGallery(restaurantId, newGallery, newActive);
    setLogoGallery(newGallery);
    if (newActive !== activeLogo) setActiveLogo(newActive);
    toast.success("Logo eliminado");
  };

  return (
    <Card className="border-none shadow-soft">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <ImageIcon className="w-5 h-5 text-primary" />
          Logos del restaurante
        </CardTitle>
        <CardDescription>
          Gestiona tu galería de logos (máx. 10). El logo activo aparece en la web y en los PDF exportados.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-4 p-3 bg-muted/30 rounded-xl">
          <div className="app-logo-glow w-16 h-16 rounded-xl overflow-hidden border-2 border-primary/30 bg-background flex items-center justify-center shrink-0">
            {activeLogo
              ? <img src={activeLogo} alt="Logo activo" className="w-full h-full object-cover" data-testid="settings-active-logo" />
              : <span className="text-2xl">🍽️</span>
            }
          </div>
          <div>
            <p className="font-medium text-sm">{activeLogo ? "Logo activo" : "Sin logo"}</p>
            <p className="text-xs text-muted-foreground">
              {activeLogo ? "Este logo aparece en tu web y PDFs" : "Sube o genera un logo en la sección Web"}
            </p>
          </div>
        </div>

        {logoGallery.length > 0 && (
          <div>
            <p className="text-xs text-muted-foreground mb-2">Galería ({logoGallery.length}/10) — clic para activar</p>
            <div className="flex flex-wrap gap-3">
              {logoGallery.map((url, idx) => (
                <div key={idx} className="relative group">
                  <button
                    type="button"
                    onClick={() => handleSetActiveLogo(url)}
                    data-testid={`settings-logo-${idx}`}
                    className={`w-16 h-16 rounded-xl overflow-hidden border-2 transition-all ${
                      activeLogo === url ? "border-primary shadow-md scale-105" : "border-border hover:border-primary/60"
                    }`}
                  >
                    <img src={url} alt={`Logo ${idx + 1}`} className="w-full h-full object-cover" />
                    {activeLogo === url && (
                      <div className="absolute inset-0 bg-primary/10 flex items-center justify-center">
                        <CheckCircle2 className="w-5 h-5 text-primary drop-shadow" />
                      </div>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRemoveLogo(url)}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-destructive text-white rounded-full hidden group-hover:flex items-center justify-center text-xs"
                    data-testid={`remove-logo-${idx}`}
                  >×</button>
                </div>
              ))}
            </div>
          </div>
        )}

        <Button
          type="button"
          variant="outline"
          onClick={() => logoInputRef.current?.click()}
          disabled={uploadingLogo}
          className="gap-2"
          data-testid="settings-upload-logo-btn"
        >
          {uploadingLogo
            ? <><Loader2 className="w-4 h-4 animate-spin" />Subiendo...</>
            : <><Plus className="w-4 h-4" />Añadir logo</>
          }
        </Button>
        <input ref={logoInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleUploadLogo} />
      </CardContent>
    </Card>
  );
}
